import { Box, BoxProps } from "@mui/material";
import { FC } from "react";
import { colors } from "../../GKVNew/styles/tokens";

interface ScrollablePanelProps extends BoxProps {
  children: React.ReactNode;
}

/**
 * Inner scroll area with a slim custom scrollbar.
 *
 * IMPORTANT: place this *inside* a container that has `overflow: hidden` and
 * the rounded corners (e.g. `TopLevelPaper`). The parent clips the corners
 * while this element does the scrolling, so the scrollbar can never square off
 * the parent's right-hand corners.
 *
 * Styling the scrollbar on the same element that owns `border-radius` does NOT
 * work reliably — macOS overlay scrollbars ignore the width and the scrollbar
 * gutter squares off the corners. The parent-clip / child-scroll split avoids
 * both problems.
 */
const ScrollablePanel: FC<ScrollablePanelProps> = ({ children, sx, ...rest }) => {
  return (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        // Firefox
        scrollbarWidth: "thin",
        scrollbarColor: `${colors.borderMedium} transparent`,
        // WebKit (Chrome/Safari/Edge)
        "&::-webkit-scrollbar": {
          width: "2px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
          margin: "12px 0",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: colors.borderMedium,
          borderRadius: "6px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: colors.borderDark,
        },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default ScrollablePanel;
